// Keeps free-gift cart lines honest. A gift line carries a `_gift_for` property
// ("product-handle:count"); it's only allowed while the cart still holds at
// least `count` of that product per gift. Reduce the trainers below that and
// the gift comes out again, so the bundle benefit can't be kept after removing
// items.
//
// The gift is taken out through the cart component's own updateQuantity() so
// the drawer, the line list and the header count all refresh exactly as they
// do for a normal customer removal.

import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

let running = false;

// A mixed-color bundle's lines carry a "Colors" label ("Purple, Pink, Gray")
// written when it was added. If units are removed or quantities change, rewrite
// the label on the remaining lines so it lists only the colors actually there.
async function syncColorLabels(cart) {
  const bundles = new Map();
  cart.items.forEach((item, index) => {
    const id = item.properties && item.properties._bundle_id;
    if (!id || !item.properties.Colors) return;
    if (!bundles.has(id)) bundles.set(id, []);
    bundles.get(id).push({ item, index });
  });

  const changes = [];
  for (const lines of bundles.values()) {
    const current = lines[0].item.properties.Colors;
    const order = current.split(',').map((color) => color.trim());
    const rank = (color) => {
      const position = order.indexOf(color);
      return position === -1 ? order.length : position;
    };

    const actual = [];
    for (const { item } of lines) {
      for (let count = 0; count < item.quantity; count++) actual.push(item.variant_title);
    }
    actual.sort((a, b) => rank(a) - rank(b));
    const label = actual.join(', ');

    if (label === current) continue;
    for (const { item, index } of lines) {
      changes.push({
        line: index + 1,
        quantity: item.quantity,
        properties: { ...item.properties, Colors: label },
      });
    }
  }

  if (changes.length === 0) return false;

  const cartItemsList = Array.from(document.querySelectorAll('cart-items-component'));
  const sectionIds = cartItemsList.map((el) => el.dataset && el.dataset.sectionId).filter(Boolean);
  let sections = null;

  for (let step = 0; step < changes.length; step++) {
    const isLast = step === changes.length - 1;
    const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...changes[step],
        ...(isLast ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {}),
      }),
    }).then((result) => result.json());
    if (isLast) sections = response.sections;
  }

  if (sections) {
    for (const el of cartItemsList) {
      const html = sections[el.dataset.sectionId];
      if (html) morphSection(el.dataset.sectionId, html, { mode: el.isDrawer ? 'hydration' : 'full' });
    }
  }

  return true;
}

// The free-bands rule (see the #gift-guard-config script in layout/theme.liquid): one free bands
// per `perGift` Kegel Exercisers, however they were added. The bundle selector adds the gift when
// a 3-pack is picked; this also covers 2 + 1, or 1 + 1 + 1, etc.
function readGiftConfig() {
  try {
    return JSON.parse(document.getElementById('gift-guard-config').textContent);
  } catch (error) {
    return null;
  }
}

async function refreshCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    return cartItems.fetchCartData();
  }

  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return response.json();
}

async function addMissingGift(cart) {
  const config = readGiftConfig();
  if (!config) return false;

  const trainers = cart.items
    .filter((item) => item.handle === config.trainerHandle)
    .reduce((total, item) => total + item.quantity, 0);
  const allowed = Math.floor(trainers / config.perGift);
  const have = cart.items
    .filter((item) => item.variant_id === config.giftVariantId)
    .reduce((total, item) => total + item.quantity, 0);
  if (allowed <= have) return false;

  const missing = allowed - have;
  const properties = {
    _gift: `Free with ${allowed * config.perGift} Kegel Exercisers`,
    _gift_for: `${config.trainerHandle}:${config.perGift}`,
  };

  // Top up the existing gift line when there is one, so the cart never ends up with several.
  const existingIndex = cart.items.findIndex(
    (item) => item.variant_id === config.giftVariantId && item.properties && item.properties._gift_for
  );
  const existing = existingIndex === -1 ? null : cart.items[existingIndex];

  // Same event the bundle selector uses, so the drawer, header count and totals all refresh. It is
  // fired from <body>: cart-items-component ignores events that come from itself.
  const deferred = CartLinesUpdateEvent.createPromise();
  document.body.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(config.giftVariantId), quantity: missing }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(existing ? `${Theme.routes.cart_change_url}.js` : Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(
        existing
          ? { line: existingIndex + 1, quantity: existing.quantity + missing }
          : { items: [{ id: config.giftVariantId, quantity: missing, properties }] }
      ),
    }).then((result) => result.json());
    if (response.status) throw new Error(response.message || 'Could not add the free gift');

    const fresh = await refreshCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fresh),
      detail: { items: fresh.items, source: 'gift-guard', sourceId: '', didError: false },
    });
    return true;
  } catch (error) {
    deferred.reject(error);
    console.warn('[gift-guard] Could not add the free gift:', error);
    return false;
  }
}

async function enforceGifts() {
  if (running) return;
  running = true;
  let changed = false;

  try {
    const cart = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());

    // Gifts are counted per rule across ALL their lines (a cart can hold more than one gift line,
    // e.g. after 6 -> 5 exercisers), and any excess is taken off the last gift line first.
    const giftLinesByRule = new Map();
    cart.items.forEach((line, index) => {
      const rule = line.properties && line.properties._gift_for;
      if (!rule) return;
      if (!giftLinesByRule.has(rule)) giftLinesByRule.set(rule, []);
      giftLinesByRule.get(rule).push({ line, index });
    });

    for (const [rule, lines] of giftLinesByRule) {
      const [handle, countText] = String(rule).split(':');
      const perGift = Number(countText);
      if (!handle || !perGift) continue;

      const qualifying = cart.items
        .filter((item) => item.handle === handle)
        .reduce((total, item) => total + item.quantity, 0);
      const allowed = Math.floor(qualifying / perGift);
      const total = lines.reduce((sum, entry) => sum + entry.line.quantity, 0);
      if (total <= allowed) continue;

      const { line, index } = lines[lines.length - 1];
      const quantity = Math.max(0, line.quantity - (total - allowed));

      const cartItems = document.querySelector('cart-items-component');
      if (cartItems && typeof cartItems.updateQuantity === 'function') {
        await customElements.whenDefined('cart-items-component');
        cartItems.updateQuantity({ line: index + 1, quantity, action: quantity > 0 ? 'change' : 'clear' });
      } else {
        await fetch(`${Theme.routes.cart_change_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: index + 1, quantity }),
        });
      }

      // Line numbers shift after a removal, so handle one line per pass and
      // re-check afterwards.
      changed = true;
      break;
    }

    // A missing gift is added after any removal has settled; then labels are checked once nothing is
    // pending (every change above re-runs this function afterwards).
    if (!changed) changed = await addMissingGift(cart);
    if (!changed) await syncColorLabels(cart);
  } catch (error) {
    console.warn('[gift-guard] Could not check gift lines:', error);
  } finally {
    running = false;
  }

  if (changed) setTimeout(enforceGifts, 1200);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.promise) event.promise.then(() => enforceGifts()).catch(() => {});
});
